import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";

/**
 * Password reset confirmation page. Reads `?token=` from the URL, POSTs
 * {token, new_password} to /api/auth/reset-password. On success we redirect
 * to /login — the backend has bumped session_epoch and revoked existing
 * refresh tokens so the user must sign in again with the new password.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tokenMissing = useMemo(() => !token || token.length < 10, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw1.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setErr("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.get("/auth/csrf");
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      <div className="w-full lg:w-1/2 flex flex-col px-12 py-10 bg-white">
        <Link to="/" className="flex items-center mb-auto">
          <Logo size={40} />
        </Link>
        <div className="max-w-sm w-full mx-auto">
          <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mb-3">/ CHOOSE A NEW PASSWORD</div>
          <h1 className="font-display font-black text-[44px] leading-[1.0]">Set it. Done.</h1>

          {tokenMissing && (
            <div
              className="mt-6 rounded-lg border border-[var(--danger)] bg-[#FEF2F2] p-4 text-[13px] text-[var(--danger)]"
              data-testid="reset-password-invalid-token"
            >
              This reset link is missing or malformed. Please request a new one.
              <div className="mt-3">
                <Link to="/forgot-password" className="font-semibold underline" data-testid="link-request-new-reset">
                  Request a new reset link
                </Link>
              </div>
            </div>
          )}

          {!tokenMissing && !done && (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="label">New password</label>
                <input
                  className="input"
                  type="password"
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-password-new"
                  autoComplete="new-password"
                />
                <div className="text-[11px] text-[var(--text-muted)] mt-1">Minimum 8 characters.</div>
              </div>
              <div>
                <label className="label">Confirm new password</label>
                <input
                  className="input"
                  type="password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  minLength={8}
                  data-testid="reset-password-confirm"
                  autoComplete="new-password"
                />
              </div>
              {err && (
                <div className="text-[13px] text-[var(--danger)] font-medium" data-testid="reset-password-error">
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="reset-password-submit"
              >
                {loading ? "Saving..." : <>Set new password <ArrowRight size={16} /></>}
              </button>
            </form>
          )}

          {done && (
            <div
              className="mt-8 rounded-lg border border-[var(--border)] bg-[#F0FDF4] p-4 space-y-2"
              data-testid="reset-password-success"
            >
              <div className="flex items-center gap-2 text-[var(--success,#166534)] font-semibold">
                <CheckCircle size={20} weight="fill" /> Password updated
              </div>
              <p className="text-[13px] text-[var(--text-secondary)]">
                All existing sessions have been signed out. Redirecting you to sign in…
              </p>
            </div>
          )}

          {!tokenMissing && !done && (
            <div className="mt-6 text-[13px]">
              <Link to="/login" className="text-[var(--accent)] font-semibold" data-testid="link-back-to-login">
                ← Back to sign in
              </Link>
            </div>
          )}
        </div>
        <div className="mt-auto font-mono text-[11px] text-[var(--text-muted)] tracking-widest">© 2026 HELLO BY FLOW</div>
      </div>

      <div className="hidden lg:flex w-1/2 bg-[var(--bg-dark)] text-white items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-[0.07]" />
        <div className="relative max-w-md">
          <div className="font-mono text-[11px] tracking-widest text-[#67E8F9] mb-4">SECURITY · POST-RESET</div>
          <h2 className="font-display font-black text-[44px] leading-[1.0]">
            Every session,<br />signed out.
          </h2>
          <p className="text-[15px] text-[#94A3B8] mt-5">
            Setting a new password revokes every active refresh token and bumps your session epoch. Nothing carries over.
          </p>
        </div>
      </div>
    </div>
  );
}
